import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  Briefcase, MapPin, Clock, TrendingUp, Users, 
  DollarSign, Calendar, Award, Target, Heart, Menu, X, Upload, CheckCircle
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import ScrollingLogo from '@/components/ScrollingLogo';

export default function CareersPage() {
  const { user, logout } = useAuth();
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Application modal state
  const [applicationModalOpen, setApplicationModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isGeneralApplication, setIsGeneralApplication] = useState(false);
  const [applicationSubmitted, setApplicationSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [applicationForm, setApplicationForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    experience: '',
    currentCompany: '',
    linkedIn: '',
    resume: null,
    coverLetter: ''
  });

  // Handle opening application modal for a specific job
  const handleApplyClick = (job) => {
    setSelectedJob(job);
    setIsGeneralApplication(false);
    setApplicationSubmitted(false);
    setApplicationModalOpen(true);
  };

  // Handle opening general application modal
  const handleGeneralApplyClick = () => {
    setSelectedJob(null);
    setIsGeneralApplication(true);
    setApplicationSubmitted(false);
    setApplicationModalOpen(true);
  };

  // Handle form input changes
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setApplicationForm(prev => ({ ...prev, [name]: value }));
  };

  // Handle file upload
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setApplicationForm(prev => ({ ...prev, resume: file }));
    }
  };

  // Handle form submission
  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call (replace with actual API integration)
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Reset form and show success
    setApplicationForm({
      fullName: '',
      email: '',
      phone: '',
      experience: '',
      currentCompany: '',
      linkedIn: '',
      resume: null,
      coverLetter: ''
    });
    setIsSubmitting(false);
    setApplicationSubmitted(true);
  };

  // Close modal and reset state
  const handleCloseModal = () => {
    setApplicationModalOpen(false);
    setSelectedJob(null);
    setIsGeneralApplication(false);
    setApplicationSubmitted(false);
  };

  // Scroll effect for transparent header
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const jobOpenings = [
    {
      id: 1,
      title: 'Event Manager',
      department: 'Events',
      location: 'Mumbai',
      type: 'Full-time',
      experience: '3-5 years',
      salary: '₹6-10 LPA',
      description: 'Lead end-to-end execution of corporate events, conferences, and exhibitions. Coordinate with clients, vendors, and internal teams.',
      requirements: [
        'Bachelor\'s degree in Event Management or related field',
        '3+ years of experience in corporate event management',
        'Strong project management and leadership skills',
        'Excellent communication and client relationship skills'
      ]
    },
    {
      id: 2,
      title: 'Senior Sales Executive - Corporate Events',
      department: 'Sales',
      location: 'Delhi / Mumbai',
      type: 'Full-time',
      experience: '4-6 years',
      salary: '₹8-15 LPA',
      description: 'Drive business development for corporate event bookings. Build relationships with Fortune 500 companies and close high-value deals.',
      requirements: [
        'Proven track record in B2B sales',
        'Experience selling to corporate clients',
        'Strong negotiation and closing skills',
        'Network in corporate sector preferred'
      ]
    },
    {
      id: 3,
      title: 'Venue Operations Coordinator',
      department: 'Operations',
      location: 'Bangalore',
      type: 'Full-time',
      experience: '2-4 years',
      salary: '₹5-8 LPA',
      description: 'Manage venue partnerships, ensure quality standards, handle logistics coordination, and oversee on-ground event operations.',
      requirements: [
        'Experience in hospitality or event operations',
        'Knowledge of venue management',
        'Strong organizational skills',
        'Ability to travel frequently'
      ]
    },
    {
      id: 4,
      title: 'Marketing Manager - Events & Hospitality',
      department: 'Marketing',
      location: 'Mumbai',
      type: 'Full-time',
      experience: '5-7 years',
      salary: '₹10-18 LPA',
      description: 'Lead marketing strategy for My Corporate Hub. Drive brand awareness, lead generation, and digital marketing campaigns.',
      requirements: [
        'Experience in B2B marketing',
        'Strong digital marketing skills',
        'Event marketing experience preferred',
        'Analytics and data-driven approach'
      ]
    },
    {
      id: 5,
      title: 'Event Coordinator',
      department: 'Events',
      location: 'Hyderabad',
      type: 'Full-time',
      experience: '1-3 years',
      salary: '₹4-6 LPA',
      description: 'Support event planning and execution. Coordinate with vendors, manage timelines, and ensure smooth event delivery.',
      requirements: [
        'Bachelor\'s degree required',
        '1+ years in event coordination',
        'Excellent organizational skills',
        'Detail-oriented and proactive'
      ]
    },
    {
      id: 6,
      title: 'Client Relationship Manager',
      department: 'Sales',
      location: 'Mumbai / Delhi',
      type: 'Full-time',
      experience: '3-5 years',
      salary: '₹7-12 LPA',
      description: 'Manage key corporate accounts, ensure client satisfaction, identify upsell opportunities, and drive repeat business.',
      requirements: [
        'Strong account management experience',
        'Excellent interpersonal skills',
        'Problem-solving mindset',
        'Corporate sector experience preferred'
      ]
    },
    {
      id: 7,
      title: 'Business Development Manager',
      department: 'Sales',
      location: 'Bangalore',
      type: 'Full-time',
      experience: '4-7 years',
      salary: '₹10-18 LPA',
      description: 'Identify new business opportunities, build strategic partnerships, and expand market presence across South India.',
      requirements: [
        'Strong business development background',
        'Experience in event or hospitality industry',
        'Strategic thinking and execution',
        'Network in corporate sector'
      ]
    },
    {
      id: 8,
      title: 'Event Production Specialist',
      department: 'Operations',
      location: 'Mumbai',
      type: 'Full-time',
      experience: '3-6 years',
      salary: '₹6-10 LPA',
      description: 'Handle technical production for events including AV, lighting, staging, and special effects. Ensure flawless execution.',
      requirements: [
        'Technical production experience',
        'Knowledge of AV equipment',
        'Vendor management skills',
        'Ability to work under pressure'
      ]
    }
  ];

  const departments = ['all', 'Events', 'Sales', 'Operations', 'Marketing'];

  const filteredJobs = selectedDepartment === 'all' 
    ? jobOpenings 
    : jobOpenings.filter(job => job.department === selectedDepartment);

  const benefits = [
    { icon: TrendingUp, title: 'Career Growth', desc: 'Fast-track career progression in India\'s leading event platform' },
    { icon: DollarSign, title: 'Competitive Pay', desc: 'Industry-leading compensation packages with performance bonuses' },
    { icon: Users, title: 'Great Team', desc: 'Work with passionate professionals in a collaborative environment' },
    { icon: Calendar, title: 'Work-Life Balance', desc: 'Flexible working arrangements and 20+ days annual leave' },
    { icon: Award, title: 'Recognition', desc: 'Employee awards, quarterly recognition programs, and incentives' },
    { icon: Heart, title: 'Health Benefits', desc: 'Comprehensive health insurance for you and your family' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/80 backdrop-blur-lg border-b border-gray-200 shadow-md' 
            : 'bg-sky-100 backdrop-blur-md border-b border-sky-200 shadow-sm'
        }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_07322645-a840-4848-8247-f88636798b30/artifacts/kyqhtx8f_MCH%20Logo-2.png" 
                alt="My Corporate Hub" 
                className="h-16 w-auto"
              />
              <span className="font-heading text-2xl text-navy font-bold tracking-tight hidden md:block">My Corporate Hub<sup className="text-xs ml-0.5">®</sup></span>
            </Link>
            
            {/* Centered Menus */}
            <div className="hidden md:flex items-center gap-8 absolute left-1/2 transform -translate-x-1/2">
              <Link to="/properties" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Properties</Link>
              <Link to="/services" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Services</Link>
              <Link to="/gifts" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Gifting</Link>
              <Link to="/event-permissions" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Event Permits</Link>
              <Link to="/trainers" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Trainers</Link>
              <Link to="/pricing" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Pricing</Link>
            </div>

            <div className="flex items-center gap-4">
              {/* Mobile Menu Button */}
              <button 
                className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6 text-navy" /> : <Menu className="w-6 h-6 text-navy" />}
              </button>
              
              {user ? (
                <>
                  <Link to={`/dashboard/${user.role}`} className="hidden md:block">
                    <Button variant="ghost" className="text-navy font-medium">Dashboard</Button>
                  </Link>
                  <Button onClick={logout} variant="outline" className="border-navy text-navy hidden md:flex">Logout</Button>
                </>
              ) : (
                <>
                  <Link to="/?login=true" className="hidden md:block">
                    <Button variant="ghost" className="text-navy font-medium">Sign In</Button>
                  </Link>
                  <Link to="/bulk-rfp/new">
                    <Button className="bg-gold hover:bg-gold-light text-navy-900 font-bold shadow-lg px-4 sm:px-6 whitespace-nowrap text-sm sm:text-base">Get Started</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
          
          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && (
            <div className="md:hidden bg-white border-t border-gray-200 py-4 px-4 space-y-2">
              <Link to="/properties" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium transition-colors" onClick={() => setMobileMenuOpen(false)}>Properties</Link>
              <Link to="/services" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium transition-colors" onClick={() => setMobileMenuOpen(false)}>Services</Link>
              <Link to="/gifts" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium transition-colors" onClick={() => setMobileMenuOpen(false)}>Gifting</Link>
              <Link to="/event-permissions" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium transition-colors" onClick={() => setMobileMenuOpen(false)}>Event Permits</Link>
              <Link to="/trainers" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium transition-colors" onClick={() => setMobileMenuOpen(false)}>Trainers</Link>
              <Link to="/pricing" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium transition-colors" onClick={() => setMobileMenuOpen(false)}>Pricing</Link>
              <div className="pt-3 border-t border-gray-200 mt-3">
                {user ? (
                  <>
                    <Link to={`/dashboard/${user.role}`} className="block py-3 px-4 bg-navy text-white rounded-lg font-medium text-center mb-2" onClick={() => setMobileMenuOpen(false)}>
                      Dashboard
                    </Link>
                    <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="block w-full py-3 px-4 border border-navy text-navy rounded-lg font-medium text-center">
                      Logout
                    </button>
                  </>
                ) : (
                  <Link to="/?login=true" className="block py-3 px-4 bg-navy text-white rounded-lg font-medium text-center" onClick={() => setMobileMenuOpen(false)}>
                    Sign In
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative text-white py-20 mt-20 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1920&q=80')" }}
        />
        <div className="absolute inset-0 bg-navy/80" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="inline-block bg-gold/10 text-gold border border-gold/30 px-6 py-2 rounded-full text-sm font-bold uppercase tracking-widest mb-6">
              JOIN OUR TEAM
            </span>
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
              Build Your Career in Events & Hospitality
            </h1>
            <p className="text-white/90 text-lg max-w-3xl mx-auto">
              Join India's fastest-growing corporate events platform. Work with Fortune 500 clients and premium 5-star properties.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Why Join Us */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-navy mb-4">Why Join My Corporate Hub?</h2>
            <p className="text-slate-800 max-w-2xl mx-auto">
              Be part of revolutionizing the corporate events industry in India
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, idx) => (
              <Card key={idx} className="border-slate-200 hover:border-gold transition-colors">
                <CardContent className="pt-6">
                  <div className="w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center mb-4">
                    <benefit.icon className="w-6 h-6 text-gold" />
                  </div>
                  <h3 className="text-lg font-bold text-navy mb-2">{benefit.title}</h3>
                  <p className="text-slate-800">{benefit.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Department Filter */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-3">
            {departments.map(dept => (
              <button
                key={dept}
                onClick={() => setSelectedDepartment(dept)}
                className={`px-6 py-2 rounded-full font-medium transition-all ${
                  selectedDepartment === dept
                    ? 'bg-navy text-white'
                    : 'bg-white text-slate-700 border border-slate-200 hover:border-navy'
                }`}
              >
                {dept === 'all' ? 'All Positions' : dept}
              </button>
            ))}
          </div>
        </div>

        {/* Job Openings */}
        <section>
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-navy mb-2">Current Openings</h2>
            <p className="text-slate-800">
              {filteredJobs.length} position{filteredJobs.length !== 1 ? 's' : ''} available
            </p>
          </div>

          <div className="space-y-6">
            {filteredJobs.map((job) => (
              <Card key={job.id} className="border-slate-200 hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div>
                      <CardTitle className="text-2xl text-navy mb-3">{job.title}</CardTitle>
                      <div className="flex flex-wrap gap-2 mb-3">
                        <Badge variant="outline" className="bg-gold/10 text-gold border-gold/30">
                          {job.department}
                        </Badge>
                        <Badge variant="outline" className="bg-navy/10 text-navy border-navy/30">
                          {job.type}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-slate-800">
                        <div className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          <span>{job.location}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Briefcase className="w-4 h-4" />
                          <span>{job.experience}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <DollarSign className="w-4 h-4" />
                          <span>{job.salary}</span>
                        </div>
                      </div>
                    </div>
                    <Button 
                      className="bg-gold hover:bg-gold-light text-navy font-bold whitespace-nowrap"
                      onClick={() => handleApplyClick(job)}
                    >
                      Apply Now
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700 mb-4">{job.description}</p>
                  <div>
                    <h4 className="font-semibold text-navy mb-2">Requirements:</h4>
                    <ul className="space-y-1">
                      {job.requirements.map((req, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-slate-800">
                          <span className="text-gold mt-1">•</span>
                          <span>{req}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Call to Action */}
        <section className="mt-16 bg-gradient-to-r from-navy to-navy-700 text-white p-8 md:p-12 rounded-xl text-center">
          <h2 className="text-3xl font-bold mb-4">Don't See Your Role?</h2>
          <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
            We're always looking for talented individuals. Send us your resume and we'll get in touch if there's a good fit.
          </p>
          <Button 
            className="bg-gold hover:bg-gold-light text-navy font-bold px-8 py-3"
            onClick={handleGeneralApplyClick}
          >
            Send Your Resume
          </Button>
        </section>
      </div>

      {/* Job Application Modal */}
      <Dialog open={applicationModalOpen} onOpenChange={handleCloseModal}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          {!applicationSubmitted ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl text-navy">
                  {isGeneralApplication ? 'Send Your Resume' : `Apply for ${selectedJob?.title}`}
                </DialogTitle>
                <DialogDescription className="text-slate-600">
                  {isGeneralApplication 
                    ? "We're always looking for talented individuals. Fill out the form below and we'll get in touch if there's a good fit."
                    : `${selectedJob?.department} • ${selectedJob?.location} • ${selectedJob?.type}`
                  }
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleSubmitApplication} className="space-y-4 mt-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="fullName" className="text-navy font-medium">Full Name *</Label>
                    <Input
                      id="fullName"
                      name="fullName"
                      value={applicationForm.fullName}
                      onChange={handleFormChange}
                      placeholder="Enter your full name"
                      required
                      className="border-slate-300 focus:border-gold focus:ring-gold"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-navy font-medium">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={applicationForm.email}
                      onChange={handleFormChange}
                      placeholder="your.email@example.com"
                      required
                      className="border-slate-300 focus:border-gold focus:ring-gold"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-navy font-medium">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={applicationForm.phone}
                      onChange={handleFormChange}
                      placeholder="+91 XXXXX XXXXX"
                      required
                      className="border-slate-300 focus:border-gold focus:ring-gold"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="experience" className="text-navy font-medium">Years of Experience *</Label>
                    <Input
                      id="experience"
                      name="experience"
                      value={applicationForm.experience}
                      onChange={handleFormChange}
                      placeholder="e.g., 3-5 years"
                      required
                      className="border-slate-300 focus:border-gold focus:ring-gold"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="currentCompany" className="text-navy font-medium">Current Company</Label>
                    <Input
                      id="currentCompany"
                      name="currentCompany"
                      value={applicationForm.currentCompany}
                      onChange={handleFormChange}
                      placeholder="Your current employer"
                      className="border-slate-300 focus:border-gold focus:ring-gold"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="linkedIn" className="text-navy font-medium">LinkedIn Profile</Label>
                    <Input
                      id="linkedIn"
                      name="linkedIn"
                      value={applicationForm.linkedIn}
                      onChange={handleFormChange}
                      placeholder="linkedin.com/in/yourprofile"
                      className="border-slate-300 focus:border-gold focus:ring-gold"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="resume" className="text-navy font-medium">Resume / CV *</Label>
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-gold transition-colors cursor-pointer">
                    <input
                      type="file"
                      id="resume"
                      name="resume"
                      accept=".pdf,.doc,.docx"
                      onChange={handleFileChange}
                      className="hidden"
                      required
                    />
                    <label htmlFor="resume" className="cursor-pointer">
                      <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                      {applicationForm.resume ? (
                        <p className="text-green-600 font-medium">{applicationForm.resume.name}</p>
                      ) : (
                        <>
                          <p className="text-slate-600 font-medium">Click to upload your resume</p>
                          <p className="text-sm text-slate-400">PDF, DOC, DOCX (Max 5MB)</p>
                        </>
                      )}
                    </label>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="coverLetter" className="text-navy font-medium">Cover Letter / Why should we hire you?</Label>
                  <Textarea
                    id="coverLetter"
                    name="coverLetter"
                    value={applicationForm.coverLetter}
                    onChange={handleFormChange}
                    placeholder="Tell us about yourself and why you're a great fit for this role..."
                    rows={4}
                    className="border-slate-300 focus:border-gold focus:ring-gold resize-none"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCloseModal}
                    className="flex-1 border-slate-300 hover:bg-slate-50"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 bg-gold hover:bg-gold-light text-navy font-bold"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Application'}
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <div className="py-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-navy mb-2">Application Submitted!</h3>
              <p className="text-slate-600 mb-6">
                Thank you for your interest in joining My Corporate Hub. 
                {selectedJob && ` Your application for ${selectedJob.title} has been received.`}
                {isGeneralApplication && ' Your resume has been received.'}
                <br />We'll review your application and get back to you soon.
              </p>
              <Button
                onClick={handleCloseModal}
                className="bg-gold hover:bg-gold-light text-navy font-bold px-8"
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Fixed Rotating Logo - Bottom Left */}
      <ScrollingLogo />
    </div>
  );
}
